import { Dialog, Fade, useTheme } from "@mui/material";
import type { FC } from "react";
import { ContactAgentDialogContactStep } from "web_myplann/Shared/Components/ContactAgentDialogContactStep";
import { ContactAgentDialogSuccessStep } from "web_myplann/Shared/Components/ContactAgentDialogSuccessStep";
import { CONTACT_DIALOG_AUTO_CLOSE_MS } from "web_myplann/Shared/constants";
import { useAutoClose } from "web_myplann/Shared/Hooks/useAutoClose";
import { useCloseOnBackPress } from "web_myplann/Shared/Hooks/useCloseOnBackPress";
import { useContactDialogStep } from "web_myplann/Shared/Hooks/useContactDialogStep";
import type { ContactDialogContent } from "web_myplann/Shared/Types/ContactDialogContent";

export const ContactAgentDialogV2: FC<ContactDialogContent> = ({
	open,
	isPending,
	onClose,
	onConfirm,
	title,
	description,
	buttonLabel,
	successTitle,
	successDescription,
}) => {
	const theme = useTheme();
	const { step, close, confirm } = useContactDialogStep({
		open,
		onClose,
		onConfirm,
		optimistic: false,
	});
	const isSuccess = step === "success";

	useAutoClose(open && isSuccess, CONTACT_DIALOG_AUTO_CLOSE_MS, close);
	useCloseOnBackPress(open, close);

	return (
		<Dialog
			open={open}
			onClose={(_, reason) => {
				if (reason === "backdropClick" && !isSuccess) return;
				close();
			}}
			slots={{ transition: Fade }}
			slotProps={{
				backdrop: { sx: { backgroundColor: theme.dialog.backdrop } },
				paper: {
					sx: {
						width: `calc(100% - ${theme.spacing(theme.spacingTokens.xl * 2)})`,
						maxWidth: theme.dialog.maxWidth,
						m: theme.spacingTokens.xl,
						p: theme.spacingTokens.xl,
						borderRadius: theme.radii.xl,
						backgroundColor: theme.palette.background.paper,
						backgroundImage: "none",
						boxShadow: theme.shadow.overlay,
					},
				},
			}}
		>
			{isSuccess ? (
				<ContactAgentDialogSuccessStep
					title={successTitle}
					description={successDescription}
				/>
			) : (
				<ContactAgentDialogContactStep
					title={title}
					description={description}
					buttonLabel={buttonLabel}
					isPending={isPending}
					onClose={close}
					onConfirm={confirm}
				/>
			)}
		</Dialog>
	);
};
